import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { doc, getDoc, updateDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { getGirlProfile, updateGirlProfile } from '../../utils/girlProfileManager';
import { 
  createGirlToMomoRequest, 
  getGirlRequests, 
  acceptTeamRequest, 
  rejectTeamRequest,
  cancelTeamRequest,
  enrichRequestsWithUserData,
  getAvailableMomo,
  REQUEST_STATUS 
} from '../../utils/teamRequestManager';
import ImageGallery from '../ImageUpload/ImageGallery';
import { getMaxImagesByTier } from '../../utils/imageUtils';
import StorageTest from '../Debug/StorageTest';
import ImageCleanup from '../Debug/ImageCleanup';
import DuplicateImageCleanup from '../Debug/DuplicateImageCleanup';
import { deleteFirstImageFromDB, deleteAllImagesFromDB } from '../../utils/directImageDelete';
import ReviewManagement from '../Review/ReviewManagement';

const GirlDashboard = () => {
  const { currentUser } = useAuth();
  const [girlProfile, setGirlProfile] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile'); // profile, schedule, bookings, revenue, team, reviews
  const [editingProfile, setEditingProfile] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [teamRequests, setTeamRequests] = useState([]);
  const [availableMomo, setAvailableMomo] = useState([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [selectedMomo, setSelectedMomo] = useState('');
  const [requestMessage, setRequestMessage] = useState('');
  const [bookingManagementMode, setBookingManagementMode] = useState('self'); // 'self', 'self_with_momo'
  const [refreshImages, setRefreshImages] = useState(0);

  // Form states
  const [profileForm, setProfileForm] = useState({
    nickname: '',
    bio: '',
    location: '',
    lineContact: '',
    lineType: 'self',
    type: 'normal',
    grades: 4,
    height: 160,
    weight: 50,
    skinTone: 'fair',
    servicePrice: 1500,
    discountType: 'none', // 'none', 'fixed', 'percentage'
    discountValue: 0
  });

  const [scheduleForm, setScheduleForm] = useState({
    isAvailable: true,
    availabilityMode: 'always', // 'always', 'temporarily_closed', 'advanced'
    unavailableDates: [], // วันที่ไม่รับงาน
    selectedMonth: new Date().getMonth(),
    selectedYear: new Date().getFullYear()
  });

  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [timeSelection, setTimeSelection] = useState({
    type: 'all_day', // 'all_day' or 'specific_time'
    startTime: '09:00',
    endTime: '22:00'
  });

  useEffect(() => {
    fetchGirlData();
    fetchTeamRequests();
    fetchAvailableMomo();
  }, [currentUser, refreshImages]);

  const fetchGirlData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // ดึงข้อมูล profile จาก girls collection
      const girlData = await getGirlProfile(currentUser.uid);
      
      // ดึงข้อมูล Tier จาก users collection
      const userRef = doc(db, 'users', currentUser.uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : {};
      
      if (girlData) {
        // รวมข้อมูล Tier เข้ากับ girlData
        const combinedData = {
          ...girlData,
          tier: userData.tier || 'free',
          tierExpiryDate: userData.tierExpiryDate,
          tierUpdatedAt: userData.tierUpdatedAt
        };
        
        setGirlProfile(combinedData);
        
        // เติมข้อมูลใน form
        setProfileForm({
          nickname: girlData.nickname || '',
          bio: girlData.bio || '',
          location: girlData.location || '',
          lineContact: girlData.lineContact || '',
          lineType: girlData.lineType || 'self',
          type: girlData.type || 'normal',
          grades: girlData.grades || 4,
          height: girlData.height || 160,
          weight: girlData.weight || 50,
          skinTone: girlData.skinTone || 'fair',
          servicePrice: girlData.servicePrice || girlData.prices?.servicePrice || 1500,
          discountType: girlData.discountType || 'none',
          discountValue: girlData.discountValue || 0
        });

        // เติมข้อมูลสถานะการจัดการการจอง
        setBookingManagementMode(girlData.bookingManagementMode || 'self');
        
        // เติมข้อมูล schedule
        if (girlData.availability) {
          // แปลงวันที่จาก string กลับเป็น Date object
          const unavailableDates = girlData.unavailableDates 
            ? girlData.unavailableDates.map(dateData => {
                if (typeof dateData === 'object' && dateData.date) {
                  return {
                    date: new Date(dateData.date),
                    type: dateData.type || 'all_day',
                    startTime: dateData.startTime || '09:00',
                    endTime: dateData.endTime || '22:00'
                  };
                }
                return new Date(dateData);
              })
            : [];

          setScheduleForm({
            isAvailable: girlData.availability.isAvailable || true,
            availabilityMode: girlData.availabilityMode || 'always',
            unavailableDates: unavailableDates,
            selectedMonth: new Date().getMonth(),
            selectedYear: new Date().getFullYear()
          });
        }
      }
      
      // ดึงการจองของตัวเอง
      const bookingsRef = collection(db, 'bookings');
      const q = query(
        bookingsRef,
        where('girlId', '==', currentUser.uid)
      );
      const snapshot = await getDocs(q);
      const bookingsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by createdAt descending
      const sortedBookings = bookingsData.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt);
        const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt);
        return dateB - dateA;
      });
      
      setBookings(sortedBookings);
      
    } catch (error) {
      console.error('Error fetching girl data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamRequests = async () => {
    if (!currentUser) return;
    
    try {
      const requests = await getGirlRequests(currentUser.uid);
      const enrichedRequests = await enrichRequestsWithUserData(requests);
      setTeamRequests(enrichedRequests);
    } catch (error) {
      console.error('Error fetching team requests:', error);
    }
  };


  const fetchAvailableMomo = async () => {
    try {
      const momos = await getAvailableMomo();
      setAvailableMomo(momos);
    } catch (error) {
      console.error('Error fetching available momo:', error);
    }
  };

  // ฟังก์ชันคำนวณราคาหลังส่วนลด
  const calculateDiscountedPrice = (originalPrice, discountType, discountValue) => {
    if (discountType === 'none' || discountValue <= 0) {
      return originalPrice;
    }
    
    if (discountType === 'fixed') {
      return Math.max(0, originalPrice - discountValue);
    }
    
    if (discountType === 'percentage') {
      const discountAmount = (originalPrice * discountValue) / 100;
      return Math.max(0, originalPrice - discountAmount);
    }
    
    return originalPrice;
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      await updateGirlProfile(currentUser.uid, profileForm);
      alert('อัปเดตข้อมูลส่วนตัวสำเร็จ!');
      setEditingProfile(false);
      fetchGirlData(); // รีเฟรชข้อมูล
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตข้อมูล');
    }
  };

  // ฟังก์ชันสำหรับรีเฟรชรูปภาพ
  const handleImageUpdate = () => {
    setRefreshImages(prev => prev + 1);
  };

  // ฟังก์ชันสำหรับรีเฟรชข้อมูล Tier
  const handleTierUpdate = () => {
    fetchGirlData();
  };

  // ฟังก์ชันสำหรับลบรูปภาพแรกโดยตรง
  const handleDeleteFirstImage = async () => {
    if (!currentUser) {
      alert('กรุณาล็อกอินก่อน');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพแรก? การกระทำนี้ไม่สามารถย้อนกลับได้!')) {
      return;
    }

    try {
      const success = await deleteFirstImageFromDB(currentUser.uid);
      if (success) {
        alert('ลบรูปภาพแรกสำเร็จ! รีเฟรชหน้าเว็บเพื่อดูการเปลี่ยนแปลง');
        fetchGirlData(); // รีเฟรชข้อมูล
      } else {
        alert('ไม่พบรูปภาพแรกหรือเกิดข้อผิดพลาด');
      }
    } catch (error) {
      console.error('Error deleting first image:', error);
      alert('เกิดข้อผิดพลาดในการลบรูปภาพแรก');
    }
  };

  // ฟังก์ชันสำหรับลบรูปภาพทั้งหมดโดยตรง
  const handleDeleteAllImages = async () => {
    if (!currentUser) {
      alert('กรุณาล็อกอินก่อน');
      return;
    }

    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบรูปภาพทั้งหมด? การกระทำนี้ไม่สามารถย้อนกลับได้!')) {
      return;
    }

    try {
      const success = await deleteAllImagesFromDB(currentUser.uid);
      if (success) {
        alert('ลบรูปภาพทั้งหมดสำเร็จ! รีเฟรชหน้าเว็บเพื่อดูการเปลี่ยนแปลง');
        fetchGirlData(); // รีเฟรชข้อมูล
      } else {
        alert('เกิดข้อผิดพลาดในการลบรูปภาพทั้งหมด');
      }
    } catch (error) {
      console.error('Error deleting all images:', error);
      alert('เกิดข้อผิดพลาดในการลบรูปภาพทั้งหมด');
    }
  };

  // คำนวณจำนวนรูปที่สามารถอัพโหลดได้ตาม Tier
  const maxImages = getMaxImagesByTier(girlProfile?.tier || 'free', 'girl');
  
  // Debug: ตรวจสอบค่า tier และ maxImages
  console.log('Girl Profile:', girlProfile);
  console.log('Girl Tier:', girlProfile?.tier);
  console.log('Max Images:', maxImages);
  console.log('Images in Firestore:', girlProfile?.images);
  console.log('Pending Images:', girlProfile?.pendingImages);
  console.log('Rejected Images:', girlProfile?.rejectedImages);

  const handleScheduleUpdate = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      // แปลงวันที่เป็น string สำหรับเก็บใน Firebase
      const unavailableDatesStrings = scheduleForm.unavailableDates.map(dateData => {
        if (typeof dateData === 'object' && dateData.date) {
          return {
            date: dateData.date.toISOString(),
            type: dateData.type,
            startTime: dateData.startTime,
            endTime: dateData.endTime
          };
        }
        return dateData.toISOString();
      });
      
      await updateGirlProfile(currentUser.uid, {
        availability: {
          isAvailable: scheduleForm.isAvailable,
          updatedAt: new Date()
        },
        status: scheduleForm.isAvailable ? 'available' : 'unavailable',
        availabilityMode: scheduleForm.availabilityMode,
        unavailableDates: unavailableDatesStrings
      });
      
      alert('อัปเดตตารางงานสำเร็จ!');
      setEditingSchedule(false);
      fetchGirlData(); // รีเฟรชข้อมูล
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตตารางงาน');
    }
  };

  // ฟังก์ชันอัปเดตสถานะการจอง
  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      const bookingRef = doc(db, 'bookings', bookingId);
      await updateDoc(bookingRef, {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: currentUser.uid,
        updatedByRole: 'girl'
      });
      
      // อัปเดตสถานะในรายการ bookings
      setBookings(prevBookings => 
        prevBookings.map(booking => 
          booking.id === bookingId 
            ? { ...booking, status: newStatus, updatedAt: new Date() }
            : booking
        )
      );
      
      alert(`อัปเดตสถานะการจองเป็น "${getStatusText(newStatus)}" สำเร็จ!`);
    } catch (error) {
      console.error('Error updating booking status:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตสถานะการจอง');
    }
  };

  // ฟังก์ชันอัปเดตสถานะการจัดการการจอง
  const updateBookingManagementMode = async (newMode) => {
    try {
      await updateGirlProfile(currentUser.uid, {
        bookingManagementMode: newMode,
        updatedAt: new Date()
      });
      
      setBookingManagementMode(newMode);
      alert(`อัปเดตสถานะการจัดการการจองเป็น "${newMode === 'self' ? 'จัดการคิวด้วยตัวเอง' : 'จัดการคิวด้วยตัวเอง และให้โมช่วยจัดการคิว'}" สำเร็จ!`);
      fetchGirlData(); // รีเฟรชข้อมูล
    } catch (error) {
      console.error('Error updating booking management mode:', error);
      alert('เกิดข้อผิดพลาดในการอัปเดตสถานะการจัดการการจอง');
    }
  };

  const handleSendTeamRequest = async (e) => {
    e.preventDefault();
    if (!selectedMomo) {
      alert('กรุณาเลือกโม');
      return;
    }

    try {
      await createGirlToMomoRequest(currentUser.uid, selectedMomo, requestMessage);
      alert('ส่งคำขอเข้าร่วมทีมสำเร็จ!');
      setShowRequestModal(false);
      setSelectedMomo('');
      setRequestMessage('');
      fetchTeamRequests(); // รีเฟรชข้อมูล
    } catch (error) {
      console.error('Error sending team request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };


  const handleAcceptRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะยอมรับคำขอเข้าร่วมทีมนี้?')) {
      return;
    }
    
    try {
      await acceptTeamRequest(requestId);
      alert('ยอมรับคำขอเข้าร่วมทีมสำเร็จ!');
      fetchTeamRequests();
      fetchGirlData(); // รีเฟรชข้อมูลโปรไฟล์
    } catch (error) {
      console.error('Error accepting request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะปฏิเสธคำขอเข้าร่วมทีมนี้?')) {
      return;
    }
    
    try {
      await rejectTeamRequest(requestId);
      alert('ปฏิเสธคำขอเข้าร่วมทีมสำเร็จ!');
      fetchTeamRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('คุณแน่ใจหรือไม่ที่จะลบคำขอนี้ออกจากรายการ?')) {
      return;
    }
    
    try {
      await cancelTeamRequest(requestId);
      alert('ลบคำขอสำเร็จ!');
      fetchTeamRequests();
    } catch (error) {
      console.error('Error cancelling request:', error);
      alert('เกิดข้อผิดพลาด: ' + error.message);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('th-TH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed': return 'ยืนยันแล้ว';
      case 'pending': return 'รอยืนยัน';
      case 'rejected': return 'ปฏิเสธ';
      case 'completed': return 'เสร็จสิ้น';
      default: return 'ไม่ทราบ';
    }
  };


  const monthNames = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
  ];

  // ฟังก์ชันแปลงเวลาเป็นรูปแบบ 24 ชั่วโมง
  const formatTime24 = (time) => {
    if (!time) return '00:00';
    // ถ้าเป็นรูปแบบ 24 ชั่วโมงอยู่แล้ว ให้คืนค่าเดิม
    if (time.includes(':')) {
      return time;
    }
    // ถ้าเป็นรูปแบบ AM/PM ให้แปลงเป็น 24 ชั่วโมง
    const [timePart, period] = time.split(' ');
    const [hours, minutes] = timePart.split(':').map(Number);
    
    if (period === 'AM') {
      if (hours === 12) return `00:${minutes.toString().padStart(2, '0')}`;
      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    } else if (period === 'PM') {
      if (hours === 12) return `12:${minutes.toString().padStart(2, '0')}`;
      return `${(hours + 12).toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
    
    return time;
  };

  // ฟังก์ชันแสดงเวลาในรูปแบบที่อ่านง่าย (24 ชั่วโมง)
  const displayTime = (time) => {
    if (!time) return '00:00';
    const formattedTime = formatTime24(time);
    const [hours, minutes] = formattedTime.split(':').map(Number);
    
    // แสดงเป็นรูปแบบ 24 ชั่วโมง พร้อมไอคอนนาฬิกา
    return `🕐 ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // ฟังก์ชันสำหรับสร้าง Calendar
  const generateCalendar = (month, year) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const calendar = [];
    const today = new Date();
    
    // เพิ่มวันว่างสำหรับวันแรกของเดือน
    for (let i = 0; i < startingDayOfWeek; i++) {
      calendar.push(null);
    }
    
    // เพิ่มวันในเดือน
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isPast = date < today;
      const isUnavailable = scheduleForm.unavailableDates.some(d => {
        if (typeof d === 'object' && d.date) {
          return d.date.getFullYear() === year && d.date.getMonth() === month && d.date.getDate() === day;
        }
        return d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
      });
      
      // ตรวจสอบว่าเป็นวันนี้หรือไม่ (เปรียบเทียบเฉพาะวันที่ ไม่รวมเวลา)
      const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const currentDate = new Date(year, month, day);
      const isToday = currentDate.getTime() === todayDate.getTime();
      
      calendar.push({
        day,
        date,
        isPast,
        isUnavailable,
        isToday
      });
    }
    
    return calendar;
  };

  // ฟังก์ชันสำหรับจัดการวันที่ไม่รับงาน
  const toggleUnavailableDate = (date) => {
    const dateStr = date.toDateString();
    
    // ตรวจสอบว่าวันที่นี้ไม่รับงานอยู่แล้วหรือไม่
    const isUnavailable = scheduleForm.unavailableDates.some(d => {
      if (typeof d === 'object' && d.date) {
        // ถ้าเป็น object ที่มี date property
        return d.date.toDateString() === dateStr;
      } else if (d instanceof Date) {
        // ถ้าเป็น Date object
        return d.toDateString() === dateStr;
      }
      return false;
    });
    
    if (isUnavailable) {
      // ถ้าเป็นวันที่ไม่รับงานอยู่แล้ว ให้ลบออก
      setScheduleForm(prev => ({
        ...prev,
        unavailableDates: prev.unavailableDates.filter(d => {
          if (typeof d === 'object' && d.date) {
            return d.date.toDateString() !== dateStr;
          } else if (d instanceof Date) {
            return d.toDateString() !== dateStr;
          }
          return true;
        })
      }));
    } else {
      // ถ้ายังไม่เป็นวันที่ไม่รับงาน ให้แสดง popup เลือกช่วงเวลา
      setSelectedDate(date);
      setTimeSelection({
        type: 'all_day',
        startTime: '09:00',
        endTime: '22:00'
      });
      setShowTimeModal(true);
    }
  };

  // ฟังก์ชันสำหรับยืนยันการเลือกช่วงเวลา
  const confirmTimeSelection = () => {
    if (selectedDate) {
      const dateWithTime = {
        date: selectedDate,
        type: timeSelection.type,
        startTime: timeSelection.startTime,
        endTime: timeSelection.endTime
      };
      
      setScheduleForm(prev => ({
        ...prev,
        unavailableDates: [...prev.unavailableDates, dateWithTime]
      }));
    }
    
    setShowTimeModal(false);
    setSelectedDate(null);
  };

  // ฟังก์ชันสำหรับยกเลิกการเลือกช่วงเวลา
  const cancelTimeSelection = () => {
    setShowTimeModal(false);
    setSelectedDate(null);
  };

  // ฟังก์ชันสำหรับกลับไปวันนี้
  const goToToday = () => {
    const today = new Date();
    setScheduleForm(prev => ({
      ...prev,
      selectedMonth: today.getMonth(),
      selectedYear: today.getFullYear()
    }));
  };

  // ฟังก์ชันสำหรับเปลี่ยนเดือน
  const changeMonth = (direction) => {
    setScheduleForm(prev => {
      let newMonth = prev.selectedMonth + direction;
      let newYear = prev.selectedYear;
      
      if (newMonth < 0) {
        newMonth = 11;
        newYear--;
      } else if (newMonth > 11) {
        newMonth = 0;
        newYear++;
      }
      
      return {
        ...prev,
        selectedMonth: newMonth,
        selectedYear: newYear
      };
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-gray-600">กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (!girlProfile) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-600">ไม่พบข้อมูลโปรไฟล์</p>
        <p className="text-sm text-gray-600 mt-2">ติดต่อแอดมินเพื่อสร้างโปรไฟล์</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-bold text-center mb-8">👧 Hugkan - แดชบอร์ดน้อง</h1>
      
      {/* สถิติสรุป */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-r from-pink-400 to-pink-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-pink-100 text-sm">สถานะ</p>
              <p className="text-lg font-bold">
                {girlProfile.status === 'available' ? 'พร้อมรับงาน' : 'ไม่พร้อมรับงาน'}
              </p>
            </div>
            <div className="text-4xl">👧</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-blue-400 to-blue-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">การจองทั้งหมด</p>
              <p className="text-2xl font-bold">{bookings.length}</p>
            </div>
            <div className="text-4xl">📋</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-400 to-green-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">รายได้รวม</p>
              <p className="text-lg font-bold">
                {formatCurrency(
                  bookings.reduce((total, booking) => {
                    return total + (booking.pricingInfo?.girlRevenue || (booking.totalPrice * 30 * 0.7));
                  }, 0)
                )}
              </p>
            </div>
            <div className="text-4xl">💰</div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-400 to-purple-600 text-white p-6 rounded-lg shadow-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">การจองวันนี้</p>
              <p className="text-2xl font-bold">
                {bookings.filter(booking => {
                  const today = new Date().toDateString();
                  const bookingDate = new Date(booking.bookingDate).toDateString();
                  return today === bookingDate;
                }).length}
              </p>
            </div>
            <div className="text-4xl">📅</div>
          </div>
        </div>
      </div>

      {/* แท็บเมนู */}
      <div className="flex space-x-4 mb-6">
        <button
          onClick={() => setActiveTab('profile')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'profile'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          👤 โปรไฟล์
        </button>
        <button
          onClick={() => setActiveTab('schedule')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'schedule'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📅 ตารางงาน
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'bookings'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          📋 การจอง
        </button>
        <button
          onClick={() => setActiveTab('booking-management')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'booking-management'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ⚙️ จัดการการจอง
        </button>
        <button
          onClick={() => setActiveTab('revenue')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'revenue'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          💰 รายได้
        </button>
        <button
          onClick={() => setActiveTab('team')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'team'
              ? 'bg-pink-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          👥 ทีม
        </button>
        
        <button
          onClick={() => setActiveTab('reviews')}
          className={`px-6 py-3 rounded-lg font-medium transition ${
            activeTab === 'reviews'
              ? 'bg-yellow-500 text-white'
              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
          }`}
        >
          ⭐ รีวิว
        </button>
      </div>

      {/* เนื้อหาตามแท็บที่เลือก */}
      {activeTab === 'profile' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">👤 ข้อมูลโปรไฟล์</h2>
              <button
                onClick={() => setEditingProfile(!editingProfile)}
                className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition"
              >
                {editingProfile ? 'ยกเลิก' : 'แก้ไข'}
              </button>
            </div>
          </div>
          
          <div className="p-6">
             {/* Storage Test Section */}
             <div className="mb-8">
               <StorageTest />
             </div>

            {/* Image Cleanup Section */}
            <div className="mb-8">
              <ImageCleanup />
            </div>

            {/* Duplicate Image Cleanup Section */}
            <div className="mb-8">
              <DuplicateImageCleanup />
            </div>


             {/* Quick Delete Section */}
             <div className="mb-8">
               <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                 <h3 className="text-lg font-semibold text-red-800 mb-4">🗑️ ลบรูปภาพด่วน</h3>
                 <p className="text-sm text-red-600 mb-4">
                   ใช้ปุ่มเหล่านี้เพื่อลบรูปภาพที่เริ่มพัฒนาหรือไม่ต้องการออกจากระบบ
                 </p>
                 <div className="flex gap-3">
                   <button
                     onClick={handleDeleteFirstImage}
                     className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition"
                   >
                     🗑️ ลบรูปภาพแรก
                   </button>
                   <button
                     onClick={handleDeleteAllImages}
                     className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                   >
                     🗑️ ลบรูปภาพทั้งหมด
                   </button>
                 </div>
                 <p className="text-xs text-red-500 mt-2">
                   ⚠️ การลบรูปภาพไม่สามารถย้อนกลับได้
                 </p>
               </div>
             </div>

            {/* Tier Information Section */}
            <div className="mb-8">
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-lg border border-purple-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-purple-800">⭐ Plan ปัจจุบัน</h3>
                  <button
                    onClick={handleTierUpdate}
                    className="px-3 py-1 bg-purple-500 text-white text-sm rounded hover:bg-purple-600 transition"
                  >
                    🔄 รีเฟรช
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">Plan ปัจจุบัน</div>
                    <div className="text-xl font-bold text-purple-600 capitalize">
                      {girlProfile?.tier || 'Free'}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">จำนวนรูปที่อัพโหลดได้</div>
                    <div className="text-xl font-bold text-blue-600">
                      {maxImages === -1 ? 'ไม่จำกัด' : `${maxImages} รูป`}
                    </div>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="text-sm text-gray-600">หมดอายุ</div>
                    <div className="text-xl font-bold text-green-600">
                      {girlProfile?.tierExpiryDate 
                        ? new Date(girlProfile.tierExpiryDate).toLocaleDateString('th-TH')
                        : 'ไม่จำกัด'
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>

             {/* Image Gallery Section */}
             <div className="mb-8">
               <ImageGallery
                 images={girlProfile?.images || []}
                 pendingImages={girlProfile?.pendingImages || []}
                 rejectedImages={girlProfile?.rejectedImages || []}
                 girlId={currentUser?.uid}
                 maxImages={maxImages}
                 isOwner={true}
                 onImageUpdate={handleImageUpdate}
                 currentTier={girlProfile?.tier || 'free'}
                 userRole="girl"
                 girlData={girlProfile}
               />
             </div>

            {editingProfile ? (
              <form onSubmit={handleProfileUpdate} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ชื่อเล่น
                    </label>
                    <input
                      type="text"
                      value={profileForm.nickname}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, nickname: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      สถานที่
                    </label>
                    <input
                      type="text"
                      value={profileForm.location}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, location: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    คำอธิบาย
                  </label>
                  <textarea
                    value={profileForm.bio}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, bio: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      LINE ID
                    </label>
                    <input
                      type="text"
                      value={profileForm.lineContact}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, lineContact: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ประเภทการติดต่อ
                    </label>
                    <select
                      value={profileForm.lineType}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, lineType: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="self">LINE น้อง</option>
                      <option value="modeling">LINE โมเดลลิ่ง</option>
                      <option value="hidden">ไม่แสดง</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ประเภทเอ็น
                    </label>
                    <select
                      value={profileForm.type}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, type: e.target.value }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value="normal">🌿 ธรรมดา</option>
                      <option value="vn">💋 วีเอ็น</option>
                      <option value="strong">💪 แรง</option>
                      <option value="up">🚀 อัพ</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      เกรด (1-5 ดาว)
                    </label>
                    <select
                      value={profileForm.grades}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, grades: parseInt(e.target.value) }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    >
                      <option value={1}>⭐ 1 ดาว</option>
                      <option value={2}>⭐⭐ 2 ดาว</option>
                      <option value={3}>⭐⭐⭐ 3 ดาว</option>
                      <option value={4}>⭐⭐⭐⭐ 4 ดาว</option>
                      <option value={5}>⭐⭐⭐⭐⭐ 5 ดาว</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ส่วนสูง (cm)
                    </label>
                    <input
                      type="number"
                      value={profileForm.height}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, height: parseInt(e.target.value) }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      min="100"
                      max="200"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      น้ำหนัก (kg)
                    </label>
                    <input
                      type="number"
                      value={profileForm.weight}
                      onChange={(e) => setProfileForm(prev => ({ ...prev, weight: parseInt(e.target.value) }))}
                      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      min="30"
                      max="100"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    สีผิว
                  </label>
                  <select
                    value={profileForm.skinTone}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, skinTone: e.target.value }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  >
                    <option value="fair">⚪ ขาวใส</option>
                    <option value="light">🟡 ขาวอมเหลือง</option>
                    <option value="medium">🟤 น้ำตาลอ่อน</option>
                    <option value="dark">⚫ คล้ำ</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ราคาค่าบริการ (บาท/ชม.)
                  </label>
                  <input
                    type="number"
                    value={profileForm.servicePrice}
                    onChange={(e) => setProfileForm(prev => ({ ...prev, servicePrice: parseInt(e.target.value) }))}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                    min="500"
                    max="10000"
                    step="100"
                  />
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-yellow-800 mb-3">💰 ส่วนลด</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ประเภทส่วนลด
                      </label>
                      <select
                        value={profileForm.discountType}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, discountType: e.target.value, discountValue: 0 }))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                      >
                        <option value="none">ไม่มีส่วนลด</option>
                        <option value="fixed">ลดเป็นจำนวนเงิน (บาท)</option>
                        <option value="percentage">ลดเป็นเปอร์เซ็นต์ (%)</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {profileForm.discountType === 'fixed' ? 'จำนวนเงินที่ลด (บาท)' : 
                         profileForm.discountType === 'percentage' ? 'เปอร์เซ็นต์ที่ลด (%)' : 'ค่าส่วนลด'}
                      </label>
                      <input
                        type="number"
                        value={profileForm.discountValue}
                        onChange={(e) => setProfileForm(prev => ({ ...prev, discountValue: parseInt(e.target.value) || 0 }))}
                        className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                        min="0"
                        max={profileForm.discountType === 'percentage' ? 100 : profileForm.servicePrice}
                        step={profileForm.discountType === 'percentage' ? 1 : 10}
                        disabled={profileForm.discountType === 'none'}
                      />
                    </div>
                  </div>

                  {/* แสดงราคาก่อนและหลังส่วนลด */}
                  {profileForm.discountType !== 'none' && profileForm.discountValue > 0 && (
                    <div className="mt-4 p-3 bg-white border border-yellow-300 rounded-lg">
                      <h4 className="font-semibold text-gray-800 mb-2">📊 สรุปราคา</h4>
                      <div className="space-y-2">
                        <div className="flex justify-between">
                          <span className="text-gray-600">ราคาเดิม:</span>
                          <span className="font-semibold">{profileForm.servicePrice.toLocaleString()} บาท/ชม.</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">ส่วนลด:</span>
                          <span className="text-red-600 font-semibold">
                            {profileForm.discountType === 'fixed' 
                              ? `${profileForm.discountValue.toLocaleString()} บาท`
                              : `${profileForm.discountValue}% (${Math.round((profileForm.servicePrice * profileForm.discountValue) / 100).toLocaleString()} บาท)`
                            }
                          </span>
                        </div>
                        <hr className="border-gray-300" />
                        <div className="flex justify-between">
                          <span className="text-gray-800 font-semibold">ราคาหลังส่วนลด:</span>
                          <span className="text-green-600 font-bold text-lg">
                            {calculateDiscountedPrice(profileForm.servicePrice, profileForm.discountType, profileForm.discountValue).toLocaleString()} บาท/ชม.
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-4">
                  <button
                    type="submit"
                    className="bg-pink-500 text-white px-6 py-2 rounded hover:bg-pink-600 transition"
                  >
                    บันทึก
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingProfile(false)}
                    className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 transition"
                  >
                    ยกเลิก
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ชื่อเล่น</label>
                    <p className="text-lg">{girlProfile.nickname}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">สถานที่</label>
                    <p className="text-lg">{girlProfile.location}</p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">คำอธิบาย</label>
                  <p className="text-lg">{girlProfile.bio}</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">LINE ID</label>
                    <p className="text-lg">@{girlProfile.lineContact}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ประเภทการติดต่อ</label>
                    <p className="text-lg">
                      {girlProfile.lineType === 'self' ? 'LINE น้อง' : 
                       girlProfile.lineType === 'modeling' ? 'LINE โมเดลลิ่ง' : 'ไม่แสดง'}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">เกรด</label>
                    <p className="text-lg">⭐⭐⭐ {girlProfile.grades}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">ประเภท</label>
                    <p className="text-lg">
                      {girlProfile.type === 'normal' ? 'ธรรมดา' : 
                       girlProfile.type === 'vn' ? 'วีเอ็น' : 
                       girlProfile.type === 'strong' ? 'แรง' : 'อัพ'}
                    </p>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">ราคาค่าบริการ</label>
                  <div className="space-y-1">
                    {girlProfile.discountType && girlProfile.discountType !== 'none' && girlProfile.discountValue > 0 ? (
                      <>
                        <p className="text-lg text-gray-500 line-through">
                          {girlProfile.servicePrice || girlProfile.prices?.servicePrice || 1500} บาท/ชม.
                        </p>
                        <p className="text-lg text-green-600 font-semibold">
                          {calculateDiscountedPrice(
                            girlProfile.servicePrice || girlProfile.prices?.servicePrice || 1500,
                            girlProfile.discountType,
                            girlProfile.discountValue
                          )} บาท/ชม.
                        </p>
                        <p className="text-sm text-red-600">
                          {girlProfile.discountType === 'fixed' 
                            ? `ลด ${girlProfile.discountValue} บาท`
                            : `ลด ${girlProfile.discountValue}%`
                          }
                        </p>
                      </>
                    ) : (
                      <p className="text-lg text-green-600 font-semibold">
                        {girlProfile.servicePrice || girlProfile.prices?.servicePrice || 1500} บาท/ชม.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'schedule' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">📅 จัดการตารางงาน</h2>
              <button
                onClick={() => setEditingSchedule(!editingSchedule)}
                className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition"
              >
                {editingSchedule ? 'ยกเลิก' : 'แก้ไข'}
              </button>
            </div>
          </div>
          
          <div className="p-6">
            {editingSchedule ? (
              <form onSubmit={handleScheduleUpdate} className="space-y-8">
                {/* สถานะการรับงาน */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold mb-4 flex items-center">
                    <span className="mr-2">🔘</span>
                    สถานะการรับงาน
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availabilityMode"
                          value="always"
                          checked={scheduleForm.availabilityMode === 'always'}
                          onChange={(e) => setScheduleForm(prev => ({ 
                            ...prev, 
                            availabilityMode: 'always',
                            isAvailable: true 
                          }))}
                          className="mr-3 w-4 h-4 text-pink-600"
                        />
                        <div className="flex items-center">
                          <span className="text-lg font-medium mr-2">เปิดรับงานตลอดเวลา</span>
                          <span className="text-sm text-gray-500">(รับงานทุกวันตามเวลาที่กำหนด)</span>
                        </div>
                      </label>
                    </div>
                    
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availabilityMode"
                          value="temporarily_closed"
                          checked={scheduleForm.availabilityMode === 'temporarily_closed'}
                          onChange={(e) => setScheduleForm(prev => ({ 
                            ...prev, 
                            availabilityMode: 'temporarily_closed',
                            isAvailable: false 
                          }))}
                          className="mr-3 w-4 h-4 text-pink-600"
                        />
                        <div className="flex items-center">
                          <span className="text-lg font-medium mr-2">ปิดรับงานชั่วคราว</span>
                          <span className="text-sm text-gray-500">(ไม่รับงานจนกว่าจะเปิดใหม่)</span>
                        </div>
                      </label>
                    </div>

                    <div className="flex items-center space-x-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="availabilityMode"
                          value="advanced"
                          checked={scheduleForm.availabilityMode === 'advanced'}
                          onChange={(e) => setScheduleForm(prev => ({ 
                            ...prev, 
                            availabilityMode: 'advanced',
                            isAvailable: true 
                          }))}
                          className="mr-3 w-4 h-4 text-pink-600"
                        />
                        <div className="flex items-center">
                          <span className="text-lg font-medium mr-2">แจ้งวันเวลาไม่รับงานล่วงหน้า</span>
                          <span className="text-sm text-gray-500">(เลือกรายวันที่ต้องการปิดรับงาน)</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Calendar สำหรับเลือกวันที่ไม่รับงาน (แสดงเฉพาะเมื่อเลือก advanced) */}
                {scheduleForm.availabilityMode === 'advanced' && (
                  <div className="bg-orange-50 p-6 rounded-lg">
                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                      <span className="mr-2">📅</span>
                      เลือกวันที่ไม่รับงาน (ล่วงหน้า 3 เดือน)
                    </h3>
                    
                    <div className="bg-white p-4 rounded-lg border border-orange-200">
                      {/* Calendar Header */}
                      <div className="flex items-center justify-between mb-4">
                        <button
                          type="button"
                          onClick={() => changeMonth(-1)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                          ←
                        </button>
                        <div className="flex items-center space-x-3">
                          <h4 className="text-lg font-semibold">
                            {monthNames[scheduleForm.selectedMonth]} {scheduleForm.selectedYear}
                          </h4>
                          <button
                            type="button"
                            onClick={goToToday}
                            className="px-3 py-1 bg-blue-500 text-white text-sm rounded-lg hover:bg-blue-600 transition"
                          >
                            📅 วันนี้
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={() => changeMonth(1)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition"
                        >
                          →
                        </button>
                      </div>
                      
                      {/* Calendar Grid */}
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'].map(day => (
                          <div key={day} className="p-2 text-center text-sm font-semibold text-gray-600">
                            {day}
                          </div>
                        ))}
                      </div>
                      
                      <div className="grid grid-cols-7 gap-1">
                        {generateCalendar(scheduleForm.selectedMonth, scheduleForm.selectedYear).map((dayData, index) => (
                          <div key={index} className="aspect-square">
                            {dayData ? (
                              <button
                                type="button"
                                onClick={() => !dayData.isPast && toggleUnavailableDate(dayData.date)}
                                disabled={dayData.isPast}
                                className={`w-full h-full p-1 rounded-lg text-sm font-medium transition ${
                                  dayData.isPast
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : dayData.isUnavailable
                                    ? 'bg-red-500 text-white hover:bg-red-600'
                                    : dayData.isToday
                                    ? 'bg-blue-500 text-white hover:bg-blue-600'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                }`}
                              >
                                {dayData.day}
                              </button>
                            ) : (
                              <div className="w-full h-full"></div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {/* Legend */}
                      <div className="mt-4 flex flex-wrap gap-4 text-sm">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                          <span>วันนี้</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-red-500 rounded mr-2"></div>
                          <span>ไม่รับงาน</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-white border border-gray-200 rounded mr-2"></div>
                          <span>รับงานปกติ</span>
                        </div>
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-gray-100 rounded mr-2"></div>
                          <span>วันที่ผ่านไปแล้ว</span>
                        </div>
                      </div>
                      
                      {/* Selected Dates Summary */}
                      {scheduleForm.unavailableDates.length > 0 && (
                        <div className="mt-4 p-3 bg-red-50 rounded-lg">
                          <h5 className="font-semibold text-red-800 mb-2">วันที่ไม่รับงานที่เลือก:</h5>
                          <div className="space-y-2">
                            {scheduleForm.unavailableDates
                              .sort((a, b) => {
                                const dateA = typeof a === 'object' && a.date ? a.date : a;
                                const dateB = typeof b === 'object' && b.date ? b.date : b;
                                return dateA - dateB;
                              })
                              .map((dateData, index) => {
                                const date = typeof dateData === 'object' && dateData.date ? dateData.date : dateData;
                                const type = typeof dateData === 'object' && dateData.type ? dateData.type : 'all_day';
                                const startTime = typeof dateData === 'object' && dateData.startTime ? dateData.startTime : null;
                                const endTime = typeof dateData === 'object' && dateData.endTime ? dateData.endTime : null;
                                
                                return (
                                  <div key={index} className="flex items-center justify-between bg-red-200 text-red-800 rounded p-2">
                                    <span className="text-sm font-medium">
                                      {date.toLocaleDateString('th-TH')}
                                    </span>
                                    <span className="text-xs">
                                      {type === 'all_day' ? 'ทั้งวัน' : `${displayTime(startTime)} - ${displayTime(endTime)}`}
                                    </span>
                                  </div>
                                );
                              })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Modal สำหรับเลือกช่วงเวลา */}
                {showTimeModal && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full mx-4">
                      <h3 className="text-lg font-semibold mb-4">
                        เลือกช่วงเวลาที่ไม่รับงาน
                      </h3>
                      
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">
                          วันที่: {selectedDate?.toLocaleDateString('th-TH')}
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="timeType"
                              value="all_day"
                              checked={timeSelection.type === 'all_day'}
                              onChange={(e) => setTimeSelection(prev => ({ ...prev, type: e.target.value }))}
                              className="mr-3"
                            />
                            <span className="font-medium">ทั้งวัน</span>
                          </label>
                        </div>
                        
                        <div>
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="radio"
                              name="timeType"
                              value="specific_time"
                              checked={timeSelection.type === 'specific_time'}
                              onChange={(e) => setTimeSelection(prev => ({ ...prev, type: e.target.value }))}
                              className="mr-3"
                            />
                            <span className="font-medium">บางช่วงเวลา</span>
                          </label>
                        </div>
                        
                        {timeSelection.type === 'specific_time' && (
                          <div className="ml-6 space-y-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                เวลาเริ่ม
                              </label>
                              <input
                                type="time"
                                value={timeSelection.startTime}
                                onChange={(e) => setTimeSelection(prev => ({ ...prev, startTime: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                              />
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                เวลาสิ้นสุด
                              </label>
                              <input
                                type="time"
                                value={timeSelection.endTime}
                                onChange={(e) => setTimeSelection(prev => ({ ...prev, endTime: e.target.value }))}
                                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex space-x-3 mt-6">
                        <button
                          type="button"
                          onClick={confirmTimeSelection}
                          className="flex-1 bg-pink-500 text-white py-2 rounded-lg hover:bg-pink-600 transition"
                        >
                          ยืนยัน
                        </button>
                        <button
                          type="button"
                          onClick={cancelTimeSelection}
                          className="flex-1 bg-gray-500 text-white py-2 rounded-lg hover:bg-gray-600 transition"
                        >
                          ยกเลิก
                        </button>
                      </div>
                    </div>
                  </div>
                )}


                
                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    className="bg-pink-500 text-white px-8 py-3 rounded-lg hover:bg-pink-600 transition font-semibold"
                  >
                    💾 บันทึกตารางงาน
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingSchedule(false)}
                    className="bg-gray-500 text-white px-8 py-3 rounded-lg hover:bg-gray-600 transition font-semibold"
                  >
                    ❌ ยกเลิก
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-6">
                {/* สถานะปัจจุบัน */}
                <div className="bg-gray-50 p-6 rounded-lg">
                  <div className="flex items-center space-x-4">
                    <span className="text-lg font-semibold">สถานะปัจจุบัน:</span>
                    <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                      girlProfile.status === 'available' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {girlProfile.status === 'available' ? '✅ เปิดรับงาน' : '❌ ปิดรับงาน'}
                    </span>
                  </div>
                  
                  {/* แสดงวันที่ไม่รับงาน (ถ้ามี) */}
                  {girlProfile.unavailableDates && girlProfile.unavailableDates.length > 0 && (
                    <div className="mt-4 p-3 bg-orange-50 rounded-lg">
                      <h5 className="font-semibold text-orange-800 mb-2">📅 วันที่ไม่รับงาน:</h5>
                      <div className="space-y-2">
                        {girlProfile.unavailableDates
                          .sort((a, b) => {
                            const dateA = typeof a === 'object' && a.date ? new Date(a.date) : new Date(a);
                            const dateB = typeof b === 'object' && b.date ? new Date(b.date) : new Date(b);
                            return dateA - dateB;
                          })
                          .map((dateData, index) => {
                            const date = typeof dateData === 'object' && dateData.date ? new Date(dateData.date) : new Date(dateData);
                            const type = typeof dateData === 'object' && dateData.type ? dateData.type : 'all_day';
                            const startTime = typeof dateData === 'object' && dateData.startTime ? dateData.startTime : null;
                            const endTime = typeof dateData === 'object' && dateData.endTime ? dateData.endTime : null;
                            
                            return (
                              <div key={index} className="flex items-center justify-between bg-orange-200 text-orange-800 rounded p-2">
                                <span className="text-sm font-medium">
                                  {date.toLocaleDateString('th-TH')}
                                </span>
                                <span className="text-xs">
                                  {type === 'all_day' ? 'ทั้งวัน' : `${displayTime(startTime)} - ${displayTime(endTime)}`}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    </div>
                  )}
                </div>
                
                
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">📋 การจองของฉัน</h2>
            <p className="text-gray-600 text-sm mt-1">
              รายการการจองทั้งหมดของคุณ
            </p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    วันที่จอง
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ลูกค้า
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    เวลา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ระยะเวลา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ราคา
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    สถานะ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    จัดการ
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(booking.bookingDate)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.userName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.bookingTime}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {booking.duration} ชม.
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="space-y-1">
                        <p className="text-green-600 font-semibold">
                          {formatCurrency(booking.pricingInfo?.totalServicePrice || booking.totalPrice * 30)}
                        </p>
                        <p className="text-blue-600 text-xs">
                          {booking.pricingInfo?.totalCoinPrice || booking.totalPrice} เหรียญ
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(booking.status)}`}>
                        {getStatusText(booking.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex space-x-2">
                        {booking.status === 'pending' && (
                          <>
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600"
                            >
                              ยืนยัน
                            </button>
                            <button
                              onClick={() => updateBookingStatus(booking.id, 'rejected')}
                              className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600"
                            >
                              ปฏิเสธ
                            </button>
                          </>
                        )}
                        {booking.status === 'confirmed' && (
                          <button
                            onClick={() => updateBookingStatus(booking.id, 'completed')}
                            className="px-3 py-1 bg-blue-500 text-white text-xs rounded hover:bg-blue-600"
                          >
                            เสร็จสิ้น
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {bookings.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              ยังไม่มีการจอง
            </div>
          )}
        </div>
      )}

      {activeTab === 'booking-management' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">⚙️ จัดการการจอง</h2>
            <p className="text-gray-600 text-sm mt-1">
              ตั้งค่าสถานะการจัดการการจองของคุณ
            </p>
          </div>
          
          <div className="p-6">
            <div className="space-y-6">
              {/* สถานะปัจจุบัน */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">สถานะการจัดการการจองปัจจุบัน</h3>
                <div className="flex items-center space-x-4">
                  <span className="text-lg font-semibold">สถานะ:</span>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                    bookingManagementMode === 'self' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {bookingManagementMode === 'self' 
                      ? '✅ จัดการคิวด้วยตัวเอง' 
                      : '✅ จัดการคิวด้วยตัวเอง และให้โมช่วยจัดการคิว'
                    }
                  </span>
                </div>
              </div>

              {/* ตัวเลือกสถานะ */}
              <div className="bg-white border border-gray-200 p-6 rounded-lg">
                <h3 className="text-lg font-semibold mb-4">เลือกสถานะการจัดการการจอง</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-4">
                    <input
                      type="radio"
                      id="self"
                      name="bookingManagementMode"
                      value="self"
                      checked={bookingManagementMode === 'self'}
                      onChange={(e) => setBookingManagementMode(e.target.value)}
                      className="w-4 h-4 text-pink-600"
                    />
                    <label htmlFor="self" className="text-sm font-medium text-gray-700">
                      <div className="font-semibold">จัดการคิวด้วยตัวเอง</div>
                      <div className="text-gray-500 text-xs mt-1">
                        คุณจะจัดการการจองทั้งหมดด้วยตัวเอง (ยืนยัน/ปฏิเสธ/เสร็จสิ้น)
                      </div>
                    </label>
                  </div>
                  
                  <div className="flex items-center space-x-4">
                    <input
                      type="radio"
                      id="self_with_momo"
                      name="bookingManagementMode"
                      value="self_with_momo"
                      checked={bookingManagementMode === 'self_with_momo'}
                      onChange={(e) => setBookingManagementMode(e.target.value)}
                      className="w-4 h-4 text-pink-600"
                    />
                    <label htmlFor="self_with_momo" className="text-sm font-medium text-gray-700">
                      <div className="font-semibold">จัดการคิวด้วยตัวเอง และให้โมช่วยจัดการคิว</div>
                      <div className="text-gray-500 text-xs mt-1">
                        คุณและโมสามารถจัดการการจองได้ทั้งคู่ (ต้องมีโมในทีมก่อน)
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className="mt-6">
                  <button
                    onClick={() => updateBookingManagementMode(bookingManagementMode)}
                    className="px-6 py-2 bg-pink-500 text-white rounded-lg hover:bg-pink-600 transition"
                  >
                    บันทึกการตั้งค่า
                  </button>
                </div>
              </div>

              {/* คำอธิบาย */}
              <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 mb-2">💡 คำอธิบาย</h4>
                <div className="space-y-2 text-sm text-blue-700">
                  <p><strong>จัดการคิวด้วยตัวเอง:</strong> คุณจะได้รับสิทธิ์ในการจัดการการจองทั้งหมดด้วยตัวเอง</p>
                  <p><strong>จัดการคิวด้วยตัวเอง และให้โมช่วยจัดการคิว:</strong> ทั้งคุณและโมสามารถจัดการการจองได้ (ต้องมีโมในทีมก่อน)</p>
                  <p><strong>หมายเหตุ:</strong> แอดมินมีสิทธิ์จัดการการจองได้ทั้งหมดอยู่แล้ว</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'revenue' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold">💰 รายได้ของฉัน</h2>
            <p className="text-gray-600 text-sm mt-1">
              สรุปรายได้จากการจองทั้งหมด
            </p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">💰 รายได้รวม</h3>
                <p className="text-2xl font-bold text-green-600">
                  {formatCurrency(
                    bookings.reduce((total, booking) => {
                      return total + (booking.pricingInfo?.girlRevenue || (booking.totalPrice * 30 * 0.7));
                    }, 0)
                  )}
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-2">📊 การจองที่เสร็จสิ้น</h3>
                <p className="text-2xl font-bold text-blue-600">
                  {bookings.filter(booking => booking.status === 'completed').length}
                </p>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="text-lg font-semibold text-purple-800 mb-2">⏳ รอยืนยัน</h3>
                <p className="text-2xl font-bold text-purple-600">
                  {bookings.filter(booking => booking.status === 'pending').length}
                </p>
              </div>
            </div>
            
            <div className="text-center text-gray-600">
              <p className="text-sm">
                💡 รายได้นี้เป็นรายได้ของคุณหลังจากหักค่าคอมมิชชั่นของระบบแล้ว
              </p>
              <p className="text-xs mt-1">
                ค่าคอมมิชชั่นของระบบ: {((girlProfile.commission || 0.3) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'team' && (
        <div className="bg-white rounded-lg shadow-md">
          <div className="p-6 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">👥 จัดการทีม</h2>
              {!girlProfile?.momoId && (
                <button
                  onClick={() => setShowRequestModal(true)}
                  className="bg-pink-500 text-white px-4 py-2 rounded hover:bg-pink-600 transition"
                >
                  ขอเข้าร่วมทีม
                </button>
              )}
            </div>
            <p className="text-gray-600 text-sm mt-1">
              {girlProfile?.momoId ? 'คุณอยู่ในทีมแล้ว' : 'คุณยังไม่ได้เข้าร่วมทีมใดๆ'}
            </p>
          </div>
          
          <div className="p-6">
            {girlProfile?.momoId ? (
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="text-lg font-semibold text-green-800 mb-2">✅ คุณอยู่ในทีมแล้ว</h3>
                <p className="text-green-700">
                  คุณได้เข้าร่วมทีมของโมแล้ว สามารถติดต่อโมเพื่อขอข้อมูลเพิ่มเติม
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* คำขอเข้าร่วมทีมที่รอยืนยัน */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">คำขอเข้าร่วมทีมที่รอยืนยัน</h3>
                  {teamRequests.filter(req => req.status === REQUEST_STATUS.PENDING).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>ไม่มีคำขอเข้าร่วมทีมที่รอยืนยัน</p>
                      <p className="text-sm">คลิกปุ่ม "ขอเข้าร่วมทีม" เพื่อส่งคำขอใหม่</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {teamRequests
                        .filter(req => req.status === REQUEST_STATUS.PENDING)
                        .map((request) => (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold">
                                {request.type === 'girl_to_momo' ? 'คำขอของคุณ' : 'คำเชิญจากโม'}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {request.type === 'girl_to_momo' 
                                  ? `ขอเข้าร่วมทีม: ${request.momoData?.displayName || request.momoData?.email}`
                                  : `เชิญจาก: ${request.momoData?.displayName || request.momoData?.email}`
                                }
                              </p>
                              {request.message && (
                                <p className="text-sm text-gray-500 mt-1">"{request.message}"</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                ส่งเมื่อ: {formatDate(request.createdAt.toDate())}
                              </p>
                            </div>
                            <div className="flex space-x-2">
                              {request.type === 'momo_to_girl' && request.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleAcceptRequest(request.id)}
                                    className="bg-green-500 text-white px-3 py-1 rounded text-sm hover:bg-green-600 transition"
                                  >
                                    ยอมรับ
                                  </button>
                                  <button
                                    onClick={() => handleRejectRequest(request.id)}
                                    className="bg-red-500 text-white px-3 py-1 rounded text-sm hover:bg-red-600 transition"
                                  >
                                    ปฏิเสธ
                                  </button>
                                </>
                              )}
                              {request.type === 'girl_to_momo' && request.status === 'pending' && (
                                <button
                                  onClick={() => handleCancelRequest(request.id)}
                                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition"
                                >
                                  ยกเลิก
                                </button>
                              )}
                              {request.status !== 'pending' && request.status !== 'cancelled' && (
                                <button
                                  onClick={() => handleCancelRequest(request.id)}
                                  className="bg-gray-500 text-white px-3 py-1 rounded text-sm hover:bg-gray-600 transition"
                                >
                                  ลบ
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* ประวัติคำขอ */}
                <div>
                  <h3 className="text-lg font-semibold mb-4">ประวัติคำขอเข้าร่วมทีม</h3>
                  {teamRequests.filter(req => req.status !== REQUEST_STATUS.PENDING).length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <p>ไม่มีประวัติคำขอเข้าร่วมทีม</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {teamRequests
                        .filter(req => req.status !== REQUEST_STATUS.PENDING)
                        .map((request) => (
                        <div key={request.id} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-semibold">
                                {request.type === 'girl_to_momo' ? 'คำขอของคุณ' : 'คำเชิญจากโม'}
                              </h4>
                              <p className="text-sm text-gray-600">
                                {request.type === 'girl_to_momo' 
                                  ? `ขอเข้าร่วมทีม: ${request.momoData?.displayName || request.momoData?.email}`
                                  : `เชิญจาก: ${request.momoData?.displayName || request.momoData?.email}`
                                }
                              </p>
                              {request.message && (
                                <p className="text-sm text-gray-500 mt-1">"{request.message}"</p>
                              )}
                              <p className="text-xs text-gray-400 mt-1">
                                ส่งเมื่อ: {formatDate(request.createdAt.toDate())}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                request.status === REQUEST_STATUS.ACCEPTED 
                                  ? 'bg-green-100 text-green-800'
                                  : request.status === REQUEST_STATUS.REJECTED
                                  ? 'bg-red-100 text-red-800'
                                  : 'bg-gray-100 text-gray-800'
                              }`}>
                                {request.status === REQUEST_STATUS.ACCEPTED ? 'ยอมรับแล้ว' :
                                 request.status === REQUEST_STATUS.REJECTED ? 'ปฏิเสธ' : 'ยกเลิก'}
                              </span>
                              {request.status !== 'cancelled' && (
                                <button
                                  onClick={() => handleCancelRequest(request.id)}
                                  className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600 transition"
                                >
                                  ลบ
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal ขอเข้าร่วมทีม */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">ส่งคำขอเข้าร่วมทีม</h3>
            
            <form onSubmit={handleSendTeamRequest} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  เลือกโม
                </label>
                <select
                  value={selectedMomo}
                  onChange={(e) => setSelectedMomo(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  required
                >
                  <option value="">เลือกโม</option>
                  {availableMomo.map(momo => (
                    <option key={momo.id} value={momo.id}>
                      {momo.displayName} ({momo.managedGirls.length} น้อง)
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ข้อความเพิ่มเติม (ไม่บังคับ)
                </label>
                <textarea
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500"
                  rows={3}
                  placeholder="บอกโมเกี่ยวกับตัวคุณ..."
                />
              </div>
              
              <div className="flex space-x-4">
                <button
                  type="submit"
                  className="flex-1 bg-pink-500 text-white py-2 rounded hover:bg-pink-600 transition"
                >
                  ส่งคำขอ
                </button>
                <button
                  type="button"
                  onClick={() => setShowRequestModal(false)}
                  className="flex-1 bg-gray-500 text-white py-2 rounded hover:bg-gray-600 transition"
                >
                  ยกเลิก
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reviews Tab */}
      {activeTab === 'reviews' && (
        <ReviewManagement />
      )}

    </div>
  );
};

export default GirlDashboard;
